import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { api } from '@/shared/api/http'
import type { BillPageOut, OrderBy } from '@/shared/api/types'

/** Origem da linha: contábil (agendamento) ou bancária (transação espelhada). */
export type BillSource = 'schedule' | 'bank'
export type BillDirection = 'payable' | 'receivable'

/**
 * Status possíveis de uma conta, nas DUAS origens.
 *
 * `overdue`/`partial`/`unknown` só existem na origem bancária — o agendamento
 * contábil vive em `open`/`settled`/`canceled`.
 */
export type BillStatus = 'open' | 'settled' | 'canceled' | 'overdue' | 'partial' | 'unknown'

/** `order_by` aceitos: a INTERSEÇÃO das duas origens (docstring do endpoint).
 * `settled_on` (data da baixa) inclusa — o processor passou a aceitá-la; conta
 * em aberto (sem baixa) vai sempre por último nas duas direções. */
export type BillOrderBy = OrderBy<'due_date' | 'amount' | 'status' | 'created_at' | 'settled_on'>

/** Visão "em aberto" — o que ainda precisa ser pago/recebido. */
export const OPEN_STATUSES: readonly BillStatus[] = ['open', 'overdue', 'partial']
/** Visão "pagas/recebidas". */
export const SETTLED_STATUSES: readonly BillStatus[] = ['settled']
/**
 * Tudo que a UI pode mostrar: todos os status MENOS `canceled`. Cancelado sumiu
 * da interface — mas o back ainda pode devolvê-lo, então o TIPO mantém
 * 'canceled' e a lista pede EXPLICITAMENTE o resto (a query nunca traz cancelado
 * em vez de depender do backend não mandar).
 */
export const VISIBLE_STATUSES: readonly BillStatus[] = [
  'open',
  'settled',
  'overdue',
  'partial',
  'unknown',
]

/** Params aceitos por `GET /v1/bills` — a fronteira do request. */
export interface BillListParams {
  readonly direction?: BillDirection
  /** REPETÍVEL: vira `?status=a&status=b`. */
  readonly status?: readonly BillStatus[]
  readonly due_after?: string
  readonly due_before?: string
  /** Data da BAIXA (recebimento/pagamento) — filtra nas duas origens. */
  readonly settled_after?: string
  readonly settled_before?: string
  /** Faixa de valor (Money como string decimal). */
  readonly amount_min?: string
  readonly amount_max?: string
  /**
   * Categoria do rateio (contábil). Só a origem contábil tem categoria — quando
   * setada, o backend EXCLUI a origem bancária (o banco não tem categoria nossa).
   */
  readonly category_id?: string
  /** Busca textual (Nome da contraparte OU Descrição) — vale nas duas origens. */
  readonly q?: string
  readonly order_by?: BillOrderBy
  readonly limit?: number
  readonly offset?: number
}

export const billKeys = {
  all: () => ['bills'] as const,
  lists: () => [...billKeys.all(), 'list'] as const,
  list: (p: BillListParams) => [...billKeys.lists(), p] as const,
} as const

/**
 * `GET /v1/bills` — as contas das DUAS origens numa lista só.
 *
 * A paginação é exata através das origens até `window_limit` (`offset + limit`
 * ≤ 1000); acima disso o backend responde 422 `bills_window_exceeded` em vez de
 * uma página silenciosamente errada.
 *
 * A mesma dívida pode aparecer nas duas origens: duplicidade é decisão de
 * PRODUTO (não há deduplicação por heurística — casar por valor+data seria
 * adivinhar, e erraria calado).
 */
export const billsQueryOptions = (p: BillListParams) =>
  queryOptions({
    queryKey: billKeys.list(p),
    queryFn: ({ signal }) => api.get<BillPageOut>('/v1/bills', { ...p }, signal),
    // Mudar filtro/página mantém as linhas antigas em vez de piscar para o
    // skeleton — o envelope traz `total`, então a paginação não salta.
    placeholderData: keepPreviousData,
  })
